import { Check, Clock3, Loader2, ShieldCheck, X } from 'lucide-react';

import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { cn } from '@/shared/utils/utils';

import { usePermissionRequestMutations, useProjectPermissionRequests } from '../hooks';
import type { ProjectPermissionRequest } from '../types';

const requesterName = (request: ProjectPermissionRequest) =>
  [request.requester.first_name, request.requester.last_name].filter(Boolean).join(' ').trim() ||
  request.requester.email;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

interface PermissionRequestsCardProps {
  projectId: number;
}

export const PermissionRequestsCard = ({ projectId }: PermissionRequestsCardProps) => {
  const { toast } = useToast();
  const highlightedId =
    Number(new URLSearchParams(window.location.search).get('permissionRequest')) || undefined;
  const { data: requests = [], isLoading, isError } = useProjectPermissionRequests(
    projectId,
    'pending',
  );
  const { reviewRequest } = usePermissionRequestMutations(projectId);

  const review = async (requestId: number, status: 'approved' | 'denied') => {
    try {
      await reviewRequest.mutateAsync({ requestId, payload: { status } });
      toast({
        title: status === 'approved' ? 'Permission granted.' : 'Permission request denied.',
        description: 'The requester has been notified.',
      });
    } catch (error) {
      toast({
        title: 'Unable to review permission request',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card data-testid="permission-requests-card">
      <CardHeader className="sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
            <CardTitle>Permission Requests</CardTitle>
          </div>
          <CardDescription className="mt-2">
            Approve one-person access without changing permissions for everyone who shares their
            role.
          </CardDescription>
        </div>
        <Badge variant="secondary" className="mt-2 w-fit sm:mt-0">
          {requests.length} pending
        </Badge>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading permission requests...
          </div>
        )}
        {isError && (
          <p className="py-8 text-center text-sm text-destructive">
            Permission requests could not be loaded.
          </p>
        )}
        {!isLoading && !isError && requests.length === 0 && (
          <div className="rounded-[13px] border border-dashed border-border px-4 py-8 text-center">
            <p className="font-medium text-foreground">No pending requests</p>
            <p className="mt-1 text-sm text-muted-foreground">
              New access requests will appear here and in your notifications.
            </p>
          </div>
        )}
        {!isLoading && !isError && requests.length > 0 && (
          <div className="divide-y divide-border rounded-[13px] border border-border">
            {requests.map((request) => (
              <article
                key={request.id}
                className={cn(
                  'p-4 transition-colors sm:p-5',
                  highlightedId === request.id && 'bg-primary/10 ring-1 ring-inset ring-primary/30',
                )}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-foreground">{requesterName(request)}</p>
                      <Badge variant="outline">{request.permission.label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{request.requester.email}</p>
                    {request.reason && (
                      <blockquote className="border-l-2 border-primary/40 pl-3 text-sm text-foreground/85">
                        {request.reason}
                      </blockquote>
                    )}
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock3 className="size-3.5" aria-hidden="true" />
                      Requested {formatDate(request.created_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={reviewRequest.isPending}
                      onClick={() => void review(request.id, 'denied')}
                    >
                      <X className="size-4" aria-hidden="true" />
                      Deny
                    </Button>
                    <Button
                      size="sm"
                      disabled={reviewRequest.isPending}
                      onClick={() => void review(request.id, 'approved')}
                    >
                      {reviewRequest.isPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Check className="size-4" aria-hidden="true" />
                      )}
                      Approve
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
